// monitor/src/auth/AuthProvider.tsx
import React, { ReactNode, useEffect } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { API_BASE } from "../apiBase";

// Runtime env injected by /env.js
declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

const env = (key: string): string | undefined => {
  const v = window.__ENV__?.[key];
  return v && v.length > 0 ? v : undefined;
};

const getAuthConfig = () => {
  // Runtime (Docker / NGINX)
  const runtimeDomain = env("AUTH0_DOMAIN");
  const runtimeClientId = env("AUTH0_CLIENT_ID");
  const runtimeAudience = env("AUTH0_AUDIENCE");
  const runtimeCallback = env("AUTH0_CALLBACK");
  const runtimeBypass = env("AUTH_BYPASS");

  // Build-time fallback (LOCAL DEV: npm start)
  const domain = runtimeDomain || process.env.REACT_APP_AUTH0_DOMAIN || "";
  const clientId = runtimeClientId || process.env.REACT_APP_AUTH0_CLIENT_ID || "";
  const audience = runtimeAudience || process.env.REACT_APP_AUTH0_AUDIENCE;
  const callbackPath = runtimeCallback || process.env.REACT_APP_AUTH0_CALLBACK || "/";
  const bypass = (runtimeBypass || process.env.REACT_APP_AUTH_BYPASS || "false") === "true";

  const redirectUri =
    callbackPath === "/" ? window.location.origin : `${window.location.origin}${callbackPath}`;

  return { domain, clientId, audience, callbackPath, bypass, redirectUri };
};

interface Props {
  children: ReactNode;
}

const TokenProviderBridge: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const origFetch = window.fetch.bind(window);

    const patchedFetch = async (input: RequestInfo, init?: RequestInit) => {
      try {
        const url = typeof input === "string" ? input : input instanceof Request ? input.url : String(input);

        if (url.startsWith(API_BASE)) {
          try {
            const token = await getAccessTokenSilently();
            if (token) {
              const headers = new Headers(init?.headers || (typeof input !== "string" && input instanceof Request ? input.headers : undefined));
              if (!headers.has("Authorization")) {
                headers.set("Authorization", `Bearer ${token}`);
              }
              const newInit = { ...(init || {}), headers } as RequestInit;
              return origFetch(input, newInit);
            }
          } catch (err) {
            console.warn("[Monitor TokenBridge] getAccessTokenSilently failed:", err);
            // fallthrough to original fetch without auth
          }
        }

        return origFetch(input, init);
      } catch (e) {
        return origFetch(input, init);
      }
    };

    // @ts-ignore
    window.fetch = patchedFetch;

    return () => {
      // @ts-ignore
      window.fetch = origFetch;
    };
  }, [getAccessTokenSilently]);

  return <>{children}</>;
};

const AuthProvider: React.FC<Props> = ({ children }) => {
  const { domain, clientId, audience, bypass, redirectUri } = getAuthConfig();

  console.log("[Monitor AuthProvider] bypass =", bypass, "env=", window.__ENV__);

  if (!domain || !clientId) {
    console.error(
      "[Monitor AuthProvider] Missing Auth0 configuration. /env.js must provide AUTH0_DOMAIN and AUTH0_CLIENT_ID.",
      { domainPresent: !!domain, clientIdPresent: !!clientId, env: window.__ENV__ }
    );
    return <>{children}</>;
  }

  if (bypass) {
    console.log("[Monitor AuthProvider] BYPASS MODE – no Auth0Provider");
    return <>{children}</>;
  }

  const authParams: Record<string, string> = { redirect_uri: redirectUri };
  if (audience) authParams.audience = audience;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authParams}
      cacheLocation="memory"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
    >
      <TokenProviderBridge>{children}</TokenProviderBridge>
    </Auth0Provider>
  );
};

export default AuthProvider;